import { sortBy } from "lodash";
import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { MedicationNotesClient } from "@/Common/Api/Patient/MedicationNotesClient";
import { PatientComplianceClient } from "@/Common/Api/Patient/PatientComplianceClient";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { PharmaNetContext, PharmaNetCredentials } from "@/Common/Components/Billing/PharmaNetCredentials";
import { HyperLinkControl } from "@/Common/Components/Buttons/HyperLinkControl";
import { VigilanceIcon } from "@/Common/Components/IconControls/VigilanceIcon";
import { DateSelector } from "@/Common/Components/Inputs/DateSelector";
import { YesNoNotApplicableTextRadioGroup } from "@/Common/Components/Inputs/YesNoNotApplicableTextRadioGroup/YesNoNotApplicableTextRadioGroup";
import { AddNotesPopupButton } from "@/Common/Components/Modals/AddNotesPopupButton";
import { Popup } from "@/Common/Components/Modals/Popup";
import { NoteListItem } from "@/Common/Components/NotesList/NotesList";
import { SpanControl } from "@/Common/Components/SpanControl";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnInit, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { Tooltip } from "@/Common/Components/Tooltip/Tooltip";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { PrescriptionHelper } from "@/Common/Helpers/PrescriptionHelper";
import { Guid } from "@/Common/Models/Guid";
import { MedicalSummaryMedicationDetailsSortColumn } from "@/Common/Models/Patient/PatientEnums";
import { SortDirection } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { MedicalItemsearchSettings } from "@/Patient/Components/MedicalItems/MedicalItemsMedicationSearchSettings";
import { DsqMedicationsTools } from "@/Patient/Components/Medication/DsqMedicationsTools";
import { PatientMedicalItemsHelper } from "@/Patient/Helpers/PatientMedicalItemsHelper";
import { VigilanceQueryViewerPopupHelper } from "@/Patient/Helpers/VigilanceQueryViewerPopupHelper";
import { MedicalItemMedicationAndPrescriptionTableItem } from "@/Patient/Models/MedicalSummary/MedicalItemMedicationAndPrescriptionTableItem";
import { MedicationDosageDisplayMode, MedicationModel } from "@/Patient/Models/Medication/MedicationModel";
import { MedicationsOrigin, MedicationsStatus } from "@/Patient/Models/UI/MedicalNote/Enums/MedicalItem";
import { IPatientMedicalSummaryModel } from "@/Patient/Models/UI/MedicalSummary/IPatientMedicalSummaryModel";
import { IMedicationGridModel } from "@/Patient/Models/UI/Medication/IPatientMedicationGridModel";
import { LongTermMedicationIcon } from "../Medication/LongTermMedicationIcon";

import "./PatientMedicationGrid.css";
import styles from "./PatientMedicationGrid.module.scss";

import img_icon_delete from "@/Globals/_images/icons/delete.png";
import img_icon_dsqicon from "@/Globals/_images/icons/DSQIcon.png";
import img_icon_refresh from "@/Globals/_images/icons/refresh-icon.png";

interface IPatientMedicationGridProps {
    PatientContactId: string;
    Summary: IPatientMedicalSummaryModel;
}

class PatientMedicationGridState {
    public ChangeEndDatePopupMedicationId: string;
    public ChangeStartDatePopupMedicationId: string;
    public FilteredMedication: MedicalItemMedicationAndPrescriptionTableItem[];
    public isPharmaNetLoginOpen: boolean;

    public static getDefault(filteredMedication: MedicalItemMedicationAndPrescriptionTableItem[]): PatientMedicationGridState {
        let state = new PatientMedicationGridState();

        state.FilteredMedication = filteredMedication;
        state.ChangeEndDatePopupMedicationId = "";
        state.ChangeStartDatePopupMedicationId = "";
        state.isPharmaNetLoginOpen = false;

        return state;
    }
}

class PatientMedicationGrid extends BaseReactComponent<IPatientMedicationGridProps, PatientMedicationGridState> {
    private Helper: MyleHelper = new MyleHelper();
    private TableCellClass: string = "text-wrap-control";
    private saveOptionKey: string = "PatientMedicationGrid";
    private isDSQLoading = false;
    private _patientStoreChangeListener: string = "";
    private patientMedicationGridModel: IMedicationGridModel = null;

    public constructor(props: IPatientMedicationGridProps) {
        super(props);

        this.patientMedicationGridModel = patientStore.PatientMedicationGridModels[props.PatientContactId];

        const sortedMedications = patientStore.filterAndSortMedication(
            props.PatientContactId,
            props.Summary?.get("MedicalItemsMedicationAndPrescription")?.toArray() ?? []
        );

        const sort = Immutable.List(
            patientStore.convertMedicationsToTableItems(
                props.PatientContactId,
                sortedMedications,
                this.patientMedicationGridModel.get("DetailsChecks")
            )
        );

        this.state = PatientMedicationGridState.getDefault(sort.toArray());
    }

    public componentDidMount(): void {
        $(window).trigger("resize");

        this._patientStoreChangeListener = patientStore.AddChangeListener(this.closeDatePopup, this, "patientMedicationGrid-change");
    }

    public componentDidUpdate(): void {
        $(window).trigger("resize");

        if (this.state != null && (this.state.ChangeEndDatePopupMedicationId || this.state.ChangeStartDatePopupMedicationId))
            $("#kendoCalendar_").data("kendoDatePicker")?.open();
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();

        patientStore.RemoveChangeListener(this._patientStoreChangeListener);
    }

    public componentWillReceiveProps(nextProp: IPatientMedicationGridProps): void {
        this.patientMedicationGridModel = patientStore.PatientMedicationGridModels[nextProp.PatientContactId];
        const filteredList = nextProp.Summary?.get("FilteredMedicalItemsMedicationAndPrescription").toArray();
        if (isArrayNullOrUndefinedOrEmpty(filteredList)) {
            this.mutateState(s => s.FilteredMedication = []);
            return;
        }

        const sort = Immutable.List(patientStore.convertMedicationsToTableItems(nextProp.PatientContactId, filteredList, this.patientMedicationGridModel.get("DetailsChecks")));
        this.mutateState(s => s.FilteredMedication = sort.toArray());
    }

    public render(): JSX.Element {
        // prepare grid
        const columnsOptions = this.Helper.loadTableColumnOptionsFromSettings(this.saveOptionKey, this.GetMyleTableColumnsDefaultOptions());
        const tableColumns = this.GetTableColumns(columnsOptions);
        const tableRenderOptions = this.LoadTableRenderOptions(columnsOptions);

        const cfgm: ColumnConfigurationModel[] = this.Helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

        return (
            <div className="medical-summary-container" data-cy="medication-grid">
                {this.state.isPharmaNetLoginOpen && (
                    <PharmaNetCredentials
                        onClose={() => this.mutateState(s => s.isPharmaNetLoginOpen = false)}
                        patient={patientStore.PatientModels[this.props.PatientContactId]}
                        context={PharmaNetContext.Patient}
                    />
                )}
                <div className="medical-summary">
                    <CommonTableHeaderComponent
                        title={cacheStore.GetString("MedicationsLabel")}
                        additionalLeftComponent={this.getProvincialMedicationButton()}
                        additionalRightComponent={[this.getPharmacoTherapeuticButton()]}
                        searchText={this.patientMedicationGridModel.get("SearchText")}
                        searchPlaceholder={cacheStore.GetString("SearchField")}
                        onSearch={text => sessionStore.dispatch(LocalPatientOperations.MedicationGrid.SearchTextChanged, { value: text })}
                        onRefreshClick={() => sessionStore.dispatch(LocalPatientOperations.MedicationGrid.RefreshMedications)}
                        onSettingsClick={() => sessionStore.dispatch(LocalPatientOperations.MedicationGrid.SettingsToggle)}
                        onPrintClick={() =>
                            patientStore
                                .ActionsMedicationTab
                                .Execute(
                                    ServerOperations.Server.Myle_Patient_Medication_GeneratePDF,
                                    {
                                        PatientContactId: this.props.PatientContactId,
                                        FromDate: this.patientMedicationGridModel.get("FromDate"),
                                        ToDate: this.patientMedicationGridModel.get("ToDate"),
                                        Statuses: this.patientMedicationGridModel.get("StatusChecks").filter(status => status.Visible && status.Checked).map(status => {
                                            switch (status.Target) {
                                                case CheckboxTarget.Myle_PatientCPP_MedicationSearch_Status_ActiveCheck: return MedicationsStatus.Active;
                                                case CheckboxTarget.Myle_PatientCPP_MedicationSearch_Status_ExpiredCheck: return MedicationsStatus.Expired;
                                                case CheckboxTarget.Myle_PatientCPP_MedicationSearch_Status_CeasedCheck: return MedicationsStatus.Ceased;
                                                case CheckboxTarget.Myle_PatientCPP_MedicationSearch_Status_CompletedCheck: return MedicationsStatus.Completed;
                                                default: return MedicationsStatus.Unknown;
                                            }
                                        }),
                                        Origins: this.patientMedicationGridModel.get("OriginChecks").filter(origin => origin.Visible && origin.Checked).map(origin => {
                                            switch (origin.Target) {
                                                case CheckboxTarget.Myle_PatientCPP_MedicationSearch_Origin_MyleCheck: return MedicationsOrigin.MYLE;
                                                case CheckboxTarget.Myle_PatientCPP_MedicationSearch_Origin_DSQCheck: return MedicationsOrigin.DSQ;
                                                case CheckboxTarget.Myle_PatientCPP_MedicationSearch_Origin_RxCheck: return MedicationsOrigin.Rx;
                                                default: return MedicationsOrigin.Unknown;
                                            }
                                        }),
                                        SortColumn: this.patientMedicationGridModel.get("SortColumn") ?? MedicalSummaryMedicationDetailsSortColumn.Medication,
                                        SortDirection: this.patientMedicationGridModel.get("SortDirection") ?? SortDirection.ASC,
                                        SearchText: this.patientMedicationGridModel.get("SearchText"),
                                        SearchRenewalFields: false
                                    }
                                )
                        }
                    />

                    {this.patientMedicationGridModel.get("IsSearchSettingsOpen") === true && (
                        <div className="filter-component medical-item-search-popup" style={{ right: "50px" }} data-cy="medicationGrid-settings">
                            <MedicalItemsearchSettings Model={this.patientMedicationGridModel} />
                        </div>
                    )}

                    <div className="scroll-limit">
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="communications_message_table"
                            Columns={tableColumns}
                            DataSource={Immutable.List(this.state.FilteredMedication)}
                            ColumnsPreferencesChangedOperation={ServerOperations.Server.Myle_UpdateUserSettings}
                            RowClickOperation={null}
                            SaveOptionskeyTarget={this.saveOptionKey}
                            ColumnReorderTarget={MyleTableTarget.Myle_PatientCPP_MedicalItemMedicationReorderClick}
                            ColumnSortTarget={MyleTableTarget.Myle_PatientCPP_MedicalItemMedicationSortClick}
                        />
                    </div>

                </div>

                {this.patientMedicationGridModel.get("IsColumnOrderingPopupOpen") && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={cfgm}
                        SaveOptionskeyTarget={this.saveOptionKey}
                        ContainerId="columnSettingsPopup"
                        CloseColumOrder={LocalPatientOperations.MedicationGrid.ColumnsSettingClose}
                    />
                )}
            </div>
        );
    }

    private getProvincialMedicationButton(): JSX.Element[] {
        const elements: JSX.Element[] = [];

        const isDsqActive = patientStore.isDSQActive() && patientStore.PatientModels[this.props.PatientContactId]?.isPatientDSQSynced();
        const hasPharmaNetAccess = cacheStore.hasPharmaNetAccess();

        if (isDsqActive || hasPharmaNetAccess) {
            elements.push(
                <div
                    style={{ display: "inherit", alignItems: "center", flexWrap: "nowrap", gap: "10px" }}
                    key="dsqAndPharmaNetAccessButtons"
                >
                    {isDsqActive
                        && [
                            <img
                                src={img_icon_dsqicon}
                                key="dsqIcon"
                            />,
                            <button
                                className="btn btn-primary"
                                onClick={() => this.onDSQActiveMedClick()}
                                key="medClickButton"
                            >
                                {cacheStore.GetString("MedActifLabel")}
                            </button>
                        ]
                    }
                    {hasPharmaNetAccess && (
                        <button
                            className="btn btn-primary"
                            onClick={() => this.mutateState(s => s.isPharmaNetLoginOpen = true)}
                        >
                            {cacheStore.GetString("PharmaNet")}
                        </button>
                    )}
                </div>
            );
        }

        return elements;
    }

    private getPharmacoTherapeuticButton(): JSX.Element {
        if (!settingsStore.configuration.get("PharmacoTherapeuticActive"))
            return null;

        return (
            <VigilanceIcon
                key="vigilanceKey"
                id="_vigilanceLink"
                height="27px"
                onClick={() => VigilanceQueryViewerPopupHelper.openVigilancePopupForSearch()}
            />
        );
    }

    private onDSQActiveMedClick(): void {
        sessionStore.dispatchMain(
            LocalOperations.MainComponentViewOperations.DisplayDSQActiveMedPopup,
            { patientContactId: this.props.PatientContactId }
        );
    }

    private LoadTableRenderOptions(ColumnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        return Immutable.fromJS({
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: ColumnOptions.filter(p => p.IsHidden === false).map(p => p.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: false,
            Autoresize: true,
            OnColumnWidthChange: null,
            HeightOffset: 45
        });
    }

    private GetMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        const columns = [
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("MedicationLabel"),
                FieldName: "Medication",
                RowClickEnabled: false,
                IsSortable: true,
                SortColumn: MedicalSummaryMedicationDetailsSortColumn.Medication,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 20
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields
                    ? cacheStore.GetString("TreatmentStartLabel")
                    : cacheStore.GetString("BeginningLabel"),
                FieldName: "Beginning",
                RowClickEnabled: false,
                IsSortable: true,
                SortColumn: MedicalSummaryMedicationDetailsSortColumn.Beginning,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("EndFieldLabel"),
                FieldName: "Ended",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: MedicalSummaryMedicationDetailsSortColumn.Ended,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("DosageLabel"),
                FieldName: "Dosage",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: MedicalSummaryMedicationDetailsSortColumn.Dosage,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 15
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("QuantityLabel"),
                FieldName: "Quantity",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: MedicalSummaryMedicationDetailsSortColumn.Quantity,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 5
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("RenewalsLabel"),
                FieldName: "Renewal",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: MedicalSummaryMedicationDetailsSortColumn.Renewals,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 5
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("StatusLabel"),
                FieldName: "Status",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: MedicalSummaryMedicationDetailsSortColumn.Status,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("OriginLabel"),
                IsHidden: true,
                FieldName: "Origin",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: MedicalSummaryMedicationDetailsSortColumn.Origin,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 5
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("ActionsLabel"),
                FieldName: "Actions",
                RowClickEnabled: false,
                IsSortable: true,
                SortColumn: MedicalSummaryMedicationDetailsSortColumn.Actions,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("Active3Label"),
                FieldName: "Active",
                RowClickEnabled: false,
                IsSortable: true,
                SortColumn: MedicalSummaryMedicationDetailsSortColumn.Active,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 5
            })
        ];

        if (cacheStore.ClinicSettings.EnableLongTermMedication) {
            columns.push(
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString("LongTermLabel"),
                    FieldName: "LongTerm",
                    RowClickEnabled: false,
                    IsSortable: true,
                    SortColumn: MedicalSummaryMedicationDetailsSortColumn.LongTerm,
                    SortDirection: SortDirection.DESC,
                    WidthPercentageDefault: 5
                })
            );
        }

        if (cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields) {
            columns.push(
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString("MedicalSummary_Medication_PrescriberLabel"),
                    FieldName: "Prescriber",
                    IsHidden: true,
                    RowClickEnabled: false,
                    IsSortable: true,
                    SortColumn: MedicalSummaryMedicationDetailsSortColumn.Prescriber,
                    SortDirection: SortDirection.ASC,
                    WidthPercentageDefault: 20
                }),
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString("MedicalSummary_Medication_WrittenDateLabel"),
                    FieldName: "WrittenDate",
                    IsHidden: true,
                    RowClickEnabled: false,
                    IsSortable: true,
                    SortColumn: MedicalSummaryMedicationDetailsSortColumn.WrittenDate,
                    SortDirection: SortDirection.ASC,
                    WidthPercentageDefault: 5
                }),
                new MyleTableColumnOptions({
                    HeaderText: cacheStore.GetString("MedicalSummary_Medication_PatientComplianceLabel"),
                    FieldName: "PatientCompliance",
                    IsHidden: true,
                    RowClickEnabled: false,
                    IsSortable: true,
                    SortColumn: MedicalSummaryMedicationDetailsSortColumn.PatientCompliance,
                    SortDirection: SortDirection.ASC,
                    WidthPercentageDefault: 10
                })
            );
        }

        return columns;
    }

    private GetTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<MedicalItemMedicationAndPrescriptionTableItem>(columnsOptions, column => {
            switch (column.FieldName) {
                case "Medication":
                    return this.BuildMedicationColumn();

                case "Prescriber":
                    return cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields
                        ? this.BuildPrescriberColumn()
                        : null;

                case "Beginning":
                    return this.BuildBeginningColumn();

                case "Ended":
                    return this.BuildEndedColumn();

                case "Dosage":
                    return this.BuildDosageColumn();

                case "Quantity":
                    return this.BuildQuantityColumn();

                case "Renewal":
                    return this.BuildRenewalColum();

                case "Status":
                    return this.BuildStatusColumn();

                case "Origin":
                    return this.BuildOriginColumn();

                case "Actions":
                    return this.BuildActionsColumn();

                case "Active":
                    return this.BuildActiveColumn();

                case "LongTerm":
                    return cacheStore.ClinicSettings.EnableLongTermMedication
                        ? this.BuildLongTermColumn()
                        : null;

                case "WrittenDate":
                    return cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields
                        ? this.BuildWrittenDateColumn()
                        : null;

                case "PatientCompliance":
                    return cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields
                        ? this.BuildPatientComplianceColumn()
                        : null;
            }
        });
    }

    private async onMedicationNoteSubmitted(medicationId: string, note: string): Promise<boolean> {
        const { isSuccess, data } = await MedicationNotesClient.addMedicationNote({
            PatientContactId: this.props.PatientContactId,
            MedicationId: medicationId,
            Note: note
        });

        if (isSuccess) {
            sessionStore.dispatch(
                LocalPatientOperations.MedicationGrid.InsertMedicationNote,
                {
                    note: data.MedicationNote,
                    medicationId,
                    patientContactId: this.props.PatientContactId
                }
            );
        }
        else {
            sessionStore.showPopup(cacheStore.GetString("UnexpectedErrorMessage"));
        }

        return isSuccess;
    }

    private BuildMedicationColumn(): MyleTableColumnInit<MedicalItemMedicationAndPrescriptionTableItem> {
        return {
            renderCell: rowData => {
                const color = this.GetRowColor(rowData.Model);
                const isRed = color === "red";
                const isGreen = color === "green";
                const display = rowData.Model.GetName();
                const displayInstruction = rowData.Model.GetInstruction();
                const displayReason = rowData.Model.getReasonWithLabel();
                const isLongTerm = rowData.Model.isLongTerm();

                if (rowData.Model.Model.CommitDate == null)
                    return (
                        <>
                            <div
                                className="display-inline-flex flex-col-gap-xs bold"
                                data-cy="PatientMedicationGrid-List"
                            >
                                {isLongTerm && <LongTermMedicationIcon dataCy="long-term-medication-icon" />}
                                <HyperLinkControl
                                    className={`${this.TableCellClass} ${rowData.Model.isExpiredLongTermMed() ? styles.expiredLongTermMedication : ""}`}
                                    id={rowData.Model.Model.Id}
                                    text={display}
                                />
                            </div>
                            {(rowData.Model.IsCeased() && rowData.ShowInstruction) && (
                                <div style={{ fontSize: "10px", fontStyle: "italic", fontWeight: 400 }}>
                                    {displayReason != null ? <SpanControl className={this.TableCellClass} text={displayReason} style={rowData.Model.IsCeased() ? { color: "red" } : {}} /> : ""}
                                    {(displayReason.length != 0) && <div />}
                                    {displayInstruction != null ? <SpanControl className={this.TableCellClass} text={displayInstruction} /> : ""}
                                </div>
                            )}
                            {((rowData.Model.IsCeased() && !rowData.ShowInstruction) || rowData.showCeased) && (
                                <div style={{ fontSize: "10px", fontStyle: "italic", fontWeight: 400 }}>
                                    <SpanControl className={this.TableCellClass} text={displayReason} style={rowData.Model.IsCeased() ? { color: "red" } : {}} />
                                </div>
                            )}
                            {(rowData.ShowInstruction && !rowData.Model.IsCeased()) && (
                                <div
                                    style={{
                                        fontSize: "10px", fontStyle: "italic", fontWeight: 400
                                    }}
                                >
                                    <SpanControl
                                        className={this.TableCellClass}
                                        text={displayInstruction}
                                    />
                                </div>
                            )}
                        </>
                    );
                else
                    return (
                        <>
                            <div
                                className="display-inline-flex flex-col-gap-xs bold"
                                data-cy="PatientMedicationGrid-List"
                            >
                                {isLongTerm && <LongTermMedicationIcon dataCy="long-term-medication-icon" />}
                                <SpanControl
                                    IsRed={isRed}
                                    IsGreen={isGreen}
                                    className={this.TableCellClass}
                                    text={display}
                                />
                            </div>
                            {(rowData.Model.IsCeased() && rowData.ShowInstruction) && (
                                <div style={{ fontSize: "10px", fontStyle: "italic", fontWeight: 400 }}>
                                    {displayReason != null ? <SpanControl className={this.TableCellClass} text={displayReason} style={rowData.Model.IsCeased() ? { color: "red" } : {}} /> : ""}
                                    {(displayReason.length != 0) && <div />}
                                    {displayInstruction != null ? <SpanControl className={this.TableCellClass} text={displayInstruction} /> : ""}
                                </div>
                            )}
                            {((rowData.Model.IsCeased() && !rowData.ShowInstruction) || rowData.showCeased) && (
                                <div style={{ fontSize: "10px", fontStyle: "italic", fontWeight: 400 }}>
                                    <SpanControl className={this.TableCellClass} text={displayReason} style={rowData.Model.IsCeased() ? { color: "red" } : {}} />
                                </div>
                            )}

                            {(rowData.ShowInstruction && !rowData.Model.IsCeased()) && (
                                <div style={{ fontSize: "10px", fontStyle: "italic", fontWeight: 400 }}>
                                    <SpanControl className={this.TableCellClass} text={displayInstruction} />
                                </div>
                            )}
                        </>
                    );
            }
        };
    }

    private BuildBeginningColumn(): MyleTableColumnInit<MedicalItemMedicationAndPrescriptionTableItem> {
        return {
            renderCell: rowData => {
                const color = this.GetRowColor(rowData.Model);
                const isRed = color === "red";
                const isGreen = color === "green";
                let display: string;

                if (cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields) {
                    display = rowData.Model.Model.RefillStartDate
                        ? PatientMedicalItemsHelper.GetLifeStageDateString(
                            rowData.Model.Model.RefillStartDate,
                            rowData.Model.Model.StartDatePrecision
                        )
                        : "";
                }
                else {
                    display = rowData.Model.Model.RefillStartDate ? moment(rowData.Model.Model.RefillStartDate).format("YYYY-MM-DD") : "";
                }

                return (
                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                        <div style={{ "width": "77px" }}>
                            <SpanControl
                                IsRed={isRed}
                                IsGreen={isGreen}
                                className={this.TableCellClass}
                                text={display}
                            />
                        </div>
                        {!rowData.Model.Model.RefillStartDate && (
                            <div
                                style={{ "marginTop": "-3px" }}
                                onClick={() => {
                                    this.mutateState(s => {
                                        if (this.state.ChangeStartDatePopupMedicationId == rowData.Model.Model.Id)
                                            s.ChangeStartDatePopupMedicationId = null;
                                        else
                                            s.ChangeStartDatePopupMedicationId = rowData.Model.Model.Id;
                                    });
                                }}
                            >
                                <span className="k-icon k-i-calendar" />
                                {this.state.ChangeStartDatePopupMedicationId == rowData.Model.Model.Id && (
                                    <div style={{ "position": "relative" }}>
                                        <Popup
                                            Id={rowData.Model.Model.Id}
                                            draggable={false}
                                            topPosition={5}
                                            leftPosition={5}
                                            altView
                                        >
                                            <div>
                                                <DateSelector
                                                    key="kendoCalendar_"
                                                    date={rowData.Model.Model.RefillStartDate}
                                                    dateFormat="yyyy-MM-dd"
                                                    disabled={false}
                                                    id="kendoCalendar_"
                                                    onChangeCallback={newDate => {
                                                        this.mutateState(
                                                            s => s.ChangeStartDatePopupMedicationId = null,
                                                            () => patientStore.ActionsMedicationTab.Execute(
                                                                ServerOperations.Server.Myle_UpdateMedicationDates,
                                                                { MedicationId: rowData.Model.Model.Id, SetStartDate: true, RefillStartDate: MyleHelper.GetStandardizedDateString(newDate) },
                                                                null,
                                                                true
                                                            )
                                                        );
                                                    }}
                                                />
                                            </div>
                                        </Popup>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            }
        };
    }

    private BuildEndedColumn(): MyleTableColumnInit<MedicalItemMedicationAndPrescriptionTableItem> {
        return {
            renderCell: rowData => {
                const color = this.GetRowColor(rowData.Model);
                let isRed = color === "red";
                let isGreen = color === "green";

                const display = rowData.Model.EffectiveEndDate() != null ? moment(rowData.Model.EffectiveEndDate()).format("YYYY-MM-DD") : "";
                if (rowData.Model.Model.CeaseDate != null) {
                    isRed = true;
                    isGreen = false;
                }
                return (
                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                        <div style={{ "width": "82px" }}>
                            <SpanControl
                                IsRed={isRed}
                                IsGreen={isGreen}
                                className={this.TableCellClass}
                                text={display}
                            />
                        </div>
                        {!rowData.Model.Model.RefillEndDate && (
                            <div
                                style={{ "marginTop": "-3px" }}
                                onClick={() => {
                                    this.mutateState(
                                        s => {
                                            if (this.state.ChangeEndDatePopupMedicationId == rowData.Model.Model.Id)
                                                s.ChangeEndDatePopupMedicationId = null;
                                            else
                                                s.ChangeEndDatePopupMedicationId = rowData.Model.Model.Id;
                                        }
                                    );
                                }}
                            >
                                <span className="k-icon k-i-calendar" />
                                {this.state.ChangeEndDatePopupMedicationId == rowData.Model.Model.Id && (
                                    <div style={{ "position": "relative" }}>
                                        <Popup
                                            Id={this.state.ChangeEndDatePopupMedicationId}
                                            draggable={false}
                                            topPosition={5}
                                            leftPosition={5}
                                            altView
                                        >
                                            <div>
                                                <DateSelector
                                                    key="kendoCalendar_"
                                                    date={rowData.Model.Model.CeaseDate != null ? rowData.Model.Model.CeaseDate : rowData.Model.Model.RefillEndDate}
                                                    dateFormat="yyyy-MM-dd"
                                                    disabled={false}
                                                    id="kendoCalendar_"
                                                    onChangeCallback={newDate => {
                                                        this.mutateState(
                                                            s => s.ChangeEndDatePopupMedicationId = null,
                                                            () => {
                                                                if (rowData.Model.Model.CeaseDate != null)
                                                                    patientStore.ActionsMedicationTab.Execute(ServerOperations.Server.Myle_UpdateMedicationDates, { MedicationId: rowData.Model.Model.Id, SetCeaseDate: true, CeaseDate: MyleHelper.GetStandardizedDateString(newDate) }, null, true);
                                                                else
                                                                    patientStore.ActionsMedicationTab.Execute(ServerOperations.Server.Myle_UpdateMedicationDates, { MedicationId: rowData.Model.Model.Id, SetEndDate: true, RefillEndDate: MyleHelper.GetStandardizedDateString(newDate) }, null, true);
                                                            }
                                                        );
                                                    }}
                                                />
                                            </div>
                                        </Popup>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            }
        };
    }

    private BuildDosageColumn(): MyleTableColumnInit<MedicalItemMedicationAndPrescriptionTableItem> {
        return {
            renderCell: rowData => {
                const color = this.GetRowColor(rowData.Model);
                const isRed = color === "red";
                const isGreen = color === "green";

                const dosageRender: JSX.Element[] = [];
                const dosages = rowData.Model.GetDosagesDisplay({ includeRenewalsOnLastDosage: false });
                dosages.forEach((x, idx) => {
                    dosageRender.push(
                        <div key={"_dosage" + idx}>
                            <SpanControl
                                IsRed={isRed}
                                IsGreen={isGreen}
                                className={this.TableCellClass}
                                text={x}
                            />
                        </div>
                    );
                });

                return (
                    <div className="dosages">
                        {dosageRender}
                    </div>
                );
            }
        };
    }

    private BuildQuantityColumn(): MyleTableColumnInit<MedicalItemMedicationAndPrescriptionTableItem> {
        return {
            renderCell: rowData => {
                const color = this.GetRowColor(rowData.Model);
                const isRed = color === "red";
                const isGreen = color === "green";
                const quantityString: string[] = rowData.Model.getMedicationQuantities();
                const quantityStringToRender: string[] = quantityString.filter(p => !IsNullOrUndefined(p));

                return (
                    <SpanControl
                        IsRed={isRed}
                        IsGreen={isGreen}
                        className={this.TableCellClass}
                        text={quantityStringToRender.map((p, idx) => <p key={p + idx}>{p}</p>)}
                    />
                );
            }
        };
    }

    private BuildRenewalColum(): MyleTableColumnInit<MedicalItemMedicationAndPrescriptionTableItem> {
        return {
            renderCell: rowData => {
                const refillString = rowData.Model.GetRenewalString();
                const color = this.GetRowColor(rowData.Model);
                const isRed = color === "red";
                const isGreen = color === "green";

                let isRecent = false;
                if (rowData.Model.Model.LastDSQSyncDate != null && moment(rowData.Model.Model.LastDSQSyncDate).isAfter(moment(Date.now()).subtract("day", 1)))
                    isRecent = true;

                const HasRefresh = !isRecent && rowData.Model.Model.DSQNumberOfRefillRemaining > 0 && patientStore.isDSQActive();

                const IsActive = (rowData.Model.Model.SummaryDisplayStatus == 1 || rowData.Model.Model.SummaryDisplayStatus == 2);
                const IsActiveDSQRefill = IsActive && isNaN(rowData.Model.Model.DSQNumberOfRefillRemaining) == false && rowData.Model.Model.NumberOfRefill > 0;

                return (
                    <div>
                        {IsActiveDSQRefill == true && (
                            <div className="flex-container" style={{ "justifyContent": "flex-start" }}>
                                <div><SpanControl IsRed={isRed} IsGreen={isGreen} className={this.TableCellClass} text="(" /></div>
                                {HasRefresh != true &&
                                    <div><a>{rowData.Model.Model.DSQNumberOfRefillRemaining.toString()}</a></div>
                                }
                                {HasRefresh == true &&
                                    <a onClick={this.OnRefreshDSQRefillClick.bind(this)}><div style={{ "backgroundImage": "url(" + img_icon_refresh + ")", "backgroundSize": "17px", "backgroundRepeat": "no-repeat", "backgroundPositionY": "0px", "backgroundPositionX": "0px", "height": "16px", "width": "16px" }} /></a>
                                }
                                <div><SpanControl IsRed={isRed} IsGreen={isGreen} className={this.TableCellClass} text={"/" + rowData.Model.Model.NumberOfRefill + ")"} /></div>
                            </div>
                        )}
                        {IsActiveDSQRefill != true &&
                            <SpanControl IsRed={isRed} IsGreen={isGreen} className={this.TableCellClass} text={refillString} />
                        }
                    </div>
                );
            }
        };
    }

    private BuildStatusColumn(): MyleTableColumnInit<MedicalItemMedicationAndPrescriptionTableItem> {
        return {
            renderCell: rowData => {
                const statusString = rowData.Model.GetStatusString();
                const color = this.GetRowColor(rowData.Model);
                const isRed = color === "red";
                const isGreen = color === "green";

                const width = (rowData.Model.Model.SummaryDisplayStatus == 1 || rowData.Model.Model.SummaryDisplayStatus == 2) ? "52px" : "77px";

                return (
                    <div className="flex-container" style={{ justifyContent: "flex-start" }}>
                        <div style={{ "width": width }}>
                            <SpanControl
                                IsRed={isRed}
                                IsGreen={isGreen}
                                className={this.TableCellClass}
                                text={statusString}
                            />
                        </div>
                        {/*
                    <div className="pointer" style={{ "marginLeft": "3px", "marginTop": "3px" }}
                        onClick={() => { this.MutateState((s: PatientMedicationGridState) => { s.ChangeStatusPopupMedicationId = rowData.Model.Model.Id }); } }>
                        <span className="glyphicon glyphicon-triangle-bottom"></span>
                    </div>
                    {self.state.ChangeStatusPopupMedicationId == rowData.Model.Model.Id &&
                        this.renderChangeStatusPopup(rowData.Model.Model.Id)
                    }
                    */}
                    </div>
                );
            }
        };
    }

    private BuildOriginColumn(): MyleTableColumnInit<MedicalItemMedicationAndPrescriptionTableItem> {
        return {
            renderCell: rowData => {
                const color = this.GetRowColor(rowData.Model);
                const isRed = color === "red";
                const isGreen = color === "green";
                const display = rowData.Model.GetOriginString();

                return (
                    <SpanControl
                        IsRed={isRed}
                        IsGreen={isGreen}
                        className={this.TableCellClass}
                        text={display}
                    />
                );
            }
        };
    }

    private BuildActionsColumn(): MyleTableColumnInit<MedicalItemMedicationAndPrescriptionTableItem> {
        return {
            renderCell: rowData => {
                const Childs: JSX.Element[] = [];
                const encounterIdContext = patientStore?.PatientUIModels[this.props.PatientContactId]?.get("EditEncounterIdContext");
                const showActionButtons = encounterIdContext || cacheStore.ClinicSettings.EnableDirectPatientProfileEdits;

                if (showActionButtons) {
                    const IsSuspendedIsActiveMed = (rowData.Model.GetStatusString() == cacheStore.GetString("SuspendedLabel") && rowData.Model.IsFutureEndDate() == true);
                    const IsUncommitetedSuspendedIsActiveMed = IsSuspendedIsActiveMed && rowData.Model.Model.CommitDate == null;

                    if (rowData.Model.Model.SummaryDisplayStatus == 1 || rowData.Model.Model.SummaryDisplayStatus == 2 || IsUncommitetedSuspendedIsActiveMed == true)
                        Childs.push(
                            <div key={"_a1" + rowData.Model.Model.Id}>
                                <HyperLinkControl
                                    className={this.TableCellClass}
                                    item={rowData.Model}
                                    operation={LocalPatientOperations.MedicationTab.UpdateMedicationPopup_Open}
                                    text={cacheStore.GetString("MiseAjourLabel")}
                                    dataCy="medicationGrid-updateButton"
                                />
                            </div>
                        );

                    if (rowData.Model.Model.CommitDate != null && IsSuspendedIsActiveMed) // Allow reactivation of suspended med
                        Childs.push(
                            <div key={"_a1" + rowData.Model.Model.Id}>
                                <HyperLinkControl
                                    className={this.TableCellClass}
                                    item={rowData.Model}
                                    text={cacheStore.GetString("ReactivateLabel")}
                                    operation={null}
                                    onChange={() => this.saveActiveMedication(rowData.Model.Model.Id, 0, encounterIdContext)}
                                />
                            </div>
                        );

                    if (cacheStore.ClinicSettings.EnableMedicationNotes) {
                        const key = "add-notes-popup";

                        if (Childs.length > 0) {
                            Childs.push(<span key={`${key}-spacer`} className={styles.spanSlashSpacing}>/</span>);
                        }

                        const medicationNotes = this.props.Summary
                            .get("MedicationNotes")
                            .getNotesByMedicationId(rowData.Model.Model.MedicalItemId);

                        const notesToLoad = medicationNotes
                            .filter(p => !p.IsLoaded)
                            .map(p => p.Id);

                        const isLoaded = !notesToLoad.any();

                        const notes: NoteListItem[] = medicationNotes
                            ?.map(note => {
                                const employee = cacheStore.GetEmployeeByContactFacetId(note.EmployeeId);

                                return {
                                    id: note.Id,
                                    date: note.CreationDate,
                                    content: note.Note,
                                    user: Employee.getFullNameWithPrefixContactFacetForEmployee(employee)
                                };
                            }) ?? [];

                        Childs.push(
                            <AddNotesPopupButton
                                key={key}
                                isDataLoaded={isLoaded}
                                onCountClick={async () => {
                                    if (!notesToLoad?.any())
                                        return;

                                    const loadedNotes = await MedicationNotesClient.getNotes(notesToLoad);

                                    sessionStore.dispatch(LocalPatientOperations.MedicationGrid.LoadMedicationNotes, {
                                        notes: loadedNotes,
                                        patientContactId: this.props.PatientContactId,
                                        medicationId: rowData.Model.Model.MedicalItemId
                                    });
                                }}
                                itemId={rowData.Model.Model.Id}
                                notes={(sortBy(notes, "date"))}
                                onSubmit={note => this.onMedicationNoteSubmitted(rowData.Model.Model.MedicalItemId, note)}
                                textColorImportant
                            />
                        );
                    }

                    if (rowData.Model.Model.CommitDate == null) {
                        Childs.push(
                            <div
                                key={"_a3" + rowData.Model.Model.Id}
                                style={{ "marginLeft": "5px" }}
                                onClick={() => { patientStore.ActionsMedicationTab.Execute(ServerOperations.Server.Myle_DeleteMedication, { MedicationId: rowData.Model.Model.Id }, null, true); }}
                            >
                                <img style={{ "width": "12px", "marginTop": "-5px", "marginLeft": "2px" }} alt="delete dosage" className="file-img" src={img_icon_delete} aria-expanded="false" data-cy="medicationGrid-deleteButton" />
                            </div>
                        );
                    }
                }

                return (<div className="flex-container" style={{ justifyContent: "flex-start" }}>{Childs}</div>);
            }
        };
    }

    private BuildActiveColumn(): MyleTableColumnInit<MedicalItemMedicationAndPrescriptionTableItem> {
        return {
            renderCell: rowData => {
                const Childs: JSX.Element[] = [];
                const encounterIdContext = patientStore?.PatientUIModels[this.props.PatientContactId]?.get("EditEncounterIdContext");
                const showActiveColumnButtons = encounterIdContext || cacheStore.ClinicSettings.EnableDirectPatientProfileEdits;

                if (showActiveColumnButtons) {
                    const IsSuspendedIsActiveMed = (rowData.Model.GetStatusString() == cacheStore.GetString("SuspendedLabel") && rowData.Model.IsFutureEndDate() == true);

                    if (IsSuspendedIsActiveMed) {
                        Childs.push(
                            <div key={"_st3" + rowData.Model.Model.Id}>
                                <span>--</span>
                            </div>
                        );
                    }
                    else if ((rowData.Model.Model.SummaryDisplayStatus == 1 || rowData.Model.Model.SummaryDisplayStatus == 2)) {
                        Childs.push(
                            <div key={"_st1" + rowData.Model.Model.Id} className="flex-container" style={{ justifyContent: "flex-start" }}>
                                <div><span>{cacheStore.GetString("YesLabel")}</span><span className={styles.spanSlashSpacing}>/</span></div>
                                <div>
                                    <HyperLinkControl
                                        className={this.TableCellClass}
                                        item={rowData.Model}
                                        text={cacheStore.GetString("NoLabel")}
                                        onChange={() => this.saveActiveMedication(rowData.Model.Model.Id, 6, encounterIdContext)}
                                    />
                                </div>
                            </div>
                        );
                    }
                    else if (rowData.Model.Model.PrescriptionStatusInt == 6 || rowData.Model.Model.PrescriptionStatusInt == 0) {
                        Childs.push(
                            <div key={"_st2" + rowData.Model.Model.Id} className="flex-container" style={{ justifyContent: "flex-start" }}>
                                <div>
                                    <HyperLinkControl
                                        className={this.TableCellClass}
                                        item={rowData.Model}
                                        text={cacheStore.GetString("YesLabel")}
                                        onChange={() => this.saveActiveMedication(rowData.Model.Model.Id, 0, encounterIdContext)}
                                    />
                                </div>
                                <div><span className={styles.spanSlashSpacing}>/</span><span>{cacheStore.GetString("NoLabel")}</span></div>
                            </div>
                        );
                    }
                    else {
                        Childs.push(
                            <div key={"_st3" + rowData.Model.Model.Id}>
                                <span>--</span>
                            </div>
                        );
                    }
                }

                return (<div className="flex-container" style={{ justifyContent: "flex-start" }}>{Childs}</div>);
            }
        };
    }

    private BuildLongTermColumn(): MyleTableColumnInit<MedicalItemMedicationAndPrescriptionTableItem> {
        const encounterIdContext = patientStore?.PatientUIModels[this.props.PatientContactId]?.get("EditEncounterIdContext");
        const showLabelButtons = encounterIdContext || cacheStore.ClinicSettings.EnableDirectPatientProfileEdits;

        return {
            renderCell: rowData => {
                let child: JSX.Element = null;
                if (showLabelButtons) {
                    if (rowData.Model.Model.IsLongTerm) {
                        child = (
                            <div className="flex-container flex-start">
                                <div><span>{cacheStore.GetString("YesLabel")}</span><span className={styles.spanSlashSpacing}>/</span></div>
                                <div>
                                    <HyperLinkControl
                                        className={this.TableCellClass}
                                        item={rowData.Model}
                                        text={cacheStore.GetString("NoLabel")}
                                        operation={null}
                                        onChange={() => this.saveLongTermMedication(rowData.Model, false, encounterIdContext)}
                                        dataCy="medicationGrid-longTerm-noButton"
                                    />
                                </div>
                            </div>
                        );
                    }
                    else {
                        child = (
                            <div className="flex-container flex-start">
                                <div>
                                    <HyperLinkControl
                                        className={this.TableCellClass}
                                        item={rowData.Model}
                                        text={cacheStore.GetString("YesLabel")}
                                        operation={null}
                                        onChange={() => this.saveLongTermMedication(rowData.Model, true, encounterIdContext)}
                                        dataCy="medicationGrid-longTerm-yesButton"
                                    />
                                </div>
                                <div><span className={styles.spanSlashSpacing}>/</span><span>{cacheStore.GetString("NoLabel")}</span></div>
                            </div>
                        );
                    }
                }

                return (<div className="flex-container flex-start">{child}</div>);
            }
        };
    }

    private BuildPrescriberColumn(): MyleTableColumnInit<MedicalItemMedicationAndPrescriptionTableItem> {
        return {
            renderCell: rowData => {
                const color = this.GetRowColor(rowData.Model);
                const isRed = color === "red";
                const isGreen = color === "green";

                const isPrescribedExternally = PrescriptionHelper.IsPrescribedExternally(rowData.Model.Model.ExternalPrescriberDisplayName);
                const display = cacheStore.ClinicSettings.EnablePrescriberDatesAndComplianceFields && isPrescribedExternally
                    ? rowData.Model.Model.ExternalPrescriberDisplayName
                    : Employee.getFullNameWithPrefixContactFacet(rowData.Model.Model.EmployeeId) ?? "";

                const id = `patient-medication-grid-prescriber-display-name-column-row-${rowData.Model.Model.Id}`;

                return (
                    <>
                        <SpanControl
                            IsRed={isRed}
                            IsGreen={isGreen}
                            className={this.TableCellClass}
                            spanId={id}
                            text={display}
                        />
                        {!IsNullOrWhiteSpace(rowData.Model.Model.ExternalPrescriberDisplayName) && (
                            <Tooltip
                                anchorSelect={`#${id}`}
                                place="bottom-end"
                            >
                                <p>
                                    {cacheStore.GetString("MedicalSummary_Medication_ExternalClinicianLabel")}
                                </p>
                            </Tooltip>
                        )}
                    </>
                );
            }
        };
    }

    private BuildWrittenDateColumn(): MyleTableColumnInit<MedicalItemMedicationAndPrescriptionTableItem> {
        return {
            renderCell: rowData => {
                const color = this.GetRowColor(rowData.Model);
                const isRed = color === "red";
                const isGreen = color === "green";
                const display = rowData.Model.Model.WrittenDate
                    ? PatientMedicalItemsHelper.GetLifeStageDateString(
                        rowData.Model.Model.WrittenDate,
                        rowData.Model.Model.WrittenDatePrecision
                    )
                    : "";

                return (
                    <div style={{ "width": "80px" }}>
                        <SpanControl
                            IsRed={isRed}
                            IsGreen={isGreen}
                            className={this.TableCellClass}
                            text={display}
                        />
                    </div>
                );
            }
        };
    }

    private BuildPatientComplianceColumn(): MyleTableColumnInit<MedicalItemMedicationAndPrescriptionTableItem> {
        return {
            renderCell: rowData => {
                const patientComplianceValue = rowData.Model.Model.IsPatientCompliant;
                const model = rowData.Model;

                return (
                    <YesNoNotApplicableTextRadioGroup
                        ariaLabel={cacheStore.GetString("MedicalSummary_Medication_PatientComplianceLabel")}
                        onChange={optionValue => this.updatePatientComplianceValue(model, optionValue)}
                        selectedValue={patientComplianceValue ?? null}
                        fieldsetId={model.Model.Id}
                    />
                );
            }
        };
    }

    private async getEncounterId(encounterId?: string): Promise<string> {
        if (!encounterId && cacheStore.ClinicSettings.EnableDirectPatientProfileEdits) {
            patientStore.IsMedicalItemFromOutsideNote = true;
            return await PatientMedicalItemsHelper.setOutsideNoteEncounter();
        }

        return encounterId;
    }

    private async saveActiveMedication(MedicationId: Guid, PrescriptionStatusInt: number, encounterIdContext?: string): Promise<void> {
        const encounterId = await this.getEncounterId(encounterIdContext);
        patientStore.ActionsMedicationTab.Execute(ServerOperations.Server.Myle_UpdateMedicationStatus, { MedicationId, PrescriptionStatusInt, EncounterId: encounterId });
    }

    private async updatePatientComplianceValue(model: MedicationModel, isPatientCompliant: boolean | null): Promise<void> {
        const medicationId = model.Model.Id;

        const response = await PatientComplianceClient.updatePatientCompliance({
            MedicationId: medicationId,
            IsPatientCompliant: isPatientCompliant
        });

        if (response.isSuccess) {
            sessionStore.dispatch(
                LocalPatientOperations.MedicationGrid.UpdatePatientComplianceValue,
                {
                    patientContactId: this.props.PatientContactId,
                    medicationId,
                    isPatientCompliant
                }
            );
        }
        else {
            sessionStore.showPopup(cacheStore.GetString("UnexpectedErrorMessage"));
        }
    }

    private async saveLongTermMedication(model: MedicationModel, isLongTerm: boolean, encounterIdContext: string): Promise<void> {
        model.Model.IsLongTerm = isLongTerm;
        model.Model.PrescriptionTab_PendingStatus = model.Model?.PrescriptionStatusInt.toString();
        model.SetModeForDosage(model.GetLastDosage().Id, MedicationDosageDisplayMode.EndDate);
        const encounterId = await this.getEncounterId(encounterIdContext);

        patientStore.ActionsMedicationTab.UpsertMedication(
            [model],
            this.props.PatientContactId,
            encounterId
        );
    }

    private GetRowColor(data: MedicationModel): string {
        const summaryDisplayStatus = data.Model?.SummaryDisplayStatus;

        switch (summaryDisplayStatus) {
            case 1: // expired
                return "red";

            case 2: // active
                return "green";

            default: // hidden
                return "";
        }
    }

    private OnRefreshDSQRefillClick(): void {
        const client = new Client();

        const requestData = {
            PatientContactId: this.props.PatientContactId,
            StartDate: null,
            EndDate: null,
            ActiveMedicationOnly: true
        };

        if (this.isDSQLoading == false) {
            this.isDSQLoading = true;

            client.PostDSQ(
                ServerOperations.Server.DSQ_MedicationSummary,
                requestData,
                response => {
                    this.isDSQLoading = false;
                    if (response.myleDSQMedications != null) {
                        if (response.Errors.length == 0) {
                            DsqMedicationsTools.InitDSQMedicationitems(response.myleDSQMedications, this.props.PatientContactId, true);
                        }
                    }

                    patientStore.ActionsMedicationTab.ReloadMedications(this.props.PatientContactId);
                },
                error => {
                    this.isDSQLoading = false;
                    console.log(error);
                }
            );
        }
    }

    private closeDatePopup(): void {
        this.mutateState(
            s => {
                s.ChangeEndDatePopupMedicationId = null;
                s.ChangeStartDatePopupMedicationId = null;
            }
        );
    }
}

export { PatientMedicationGrid };
